import {
  ChangeDetectorRef, Component, ElementRef, HostListener, Inject, type OnDestroy, type OnInit, ViewChild
} from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { SharedService } from '../../../../services/shared.service'
import {
  type VehicleGalleryDTO, GalleryService
} from '../../../../services/gallery.service'
import { type VehicleDTOExtended } from '../../../../services/vehicles.service'
import { ResizeService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { NgxGlideComponent, NgxGlideModule } from 'ngx-glide'

import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
import { VehicleProfilePictureComponent } from '../../content/vehicle-profile-picture/vehicle-profile-picture.component'

@Component({
  selector: 'customer-portal-app-revolution-image-slider',
  standalone: true,
  templateUrl: './image-slider.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule,
    VehicleProfilePictureComponent
  ]
})
export class RevolutionImageSliderComponent implements OnInit, OnDestroy {
  index: number
  imageArr: VehicleGalleryDTO[]
  defaultTouch = { x: 0, y: 0, time: 0 }
  vehicle: VehicleDTOExtended

  sub = {
    onVehicleProfilePictureChanged: null,
    onResizePx: null
  }

  // glider
  @ViewChild('RevolutionImageSliderComponent_ngxGlide')
    ngxGlide!: NgxGlideComponent

  @ViewChild('sliderImage')
    sliderImage?: ElementRef<HTMLImageElement>

  startAt
  showArrows = false
  boundAndFitGlider = false
  maxSlides = 0
  public screenSizePx: number

  constructor (
    public dialogRef: MatDialogRef<RevolutionImageSliderComponent>,
    @Inject(MAT_DIALOG_DATA) data,
    private readonly cdf: ChangeDetectorRef,
    private readonly galleryService: GalleryService,
    private readonly sharedService: SharedService,
    public resizeService: ResizeService
  ) {
    this.screenSizePx = this.resizeService.screenSizePx
    this.index = data.index
    this.imageArr = data.imageArray
    this.vehicle = data.vehicle
  }

  ngOnDestroy (): void {
    this.sharedService.unsubscribe(this.sub)
  }

  ngOnInit (): void {
    this.sub.onVehicleProfilePictureChanged =
      this.galleryService.onVehicleProfilePictureChanged.subscribe(
        profilePicture => {
          this.close()
        }
      )

    this.sub.onResizePx = this.resizeService.onResizePx.subscribe(size => {
      this.screenSizePx = size
      this.setGlider()
      this.recreate()
    })

    this.setGlider()
    this.recreate()
  }

  back (): void {
    if (this.index === 0) {
      this.index = this.imageArr.length - 1
    } else {
      this.index = this.index - 1
    }
    this.setStartAt()
    this.animateSlide('left')
  }

  forward (): void {
    if (this.index === this.imageArr.length - 1) {
      this.index = 0
    } else {
      this.index = this.index + 1
    }
    this.setStartAt()
    this.animateSlide('right')
  }

  private animateSlide (direction: 'left' | 'right'): void {
    const el = this.sliderImage?.nativeElement
    if (el == null || typeof el.animate !== 'function') return
    const from = direction === 'right' ? '100%' : '-100%'
    el.animate(
      [
        { transform: `translateX(${from})` },
        { transform: 'translateX(0)' }
      ],
      { duration: 300, easing: 'ease-out' }
    )
  }

  setImage (index): void {
    this.index = index
  }

  close (reloadGallery?: boolean): void {
    this.dialogRef.close(reloadGallery)
  }

  @HostListener('touchstart', ['$event'])
  // @HostListener('touchmove', ['$event'])
  @HostListener('touchend', ['$event'])
  @HostListener('touchcancel', ['$event'])
  handleTouch (event): void {
    const touch =
      event.touches[0] != null ? event.touches[0] : event.changedTouches[0]

    // check the events
    if (event.type === 'touchstart') {
      this.defaultTouch.x = touch.pageX
      this.defaultTouch.y = touch.pageY
      this.defaultTouch.time = event.timeStamp
    } else if (event.type === 'touchend') {
      const deltaX = touch.pageX - this.defaultTouch.x
      const deltaTime = event.timeStamp - this.defaultTouch.time

      // simulte a swipe -> less than 500 ms and more than 60 px
      if (deltaTime < 500) {
        // touch movement lasted less than 500 ms
        if (Math.abs(deltaX) > 60) {
          // delta x is at least 60 pixels
          if (deltaX > 0) {
            this.back()
          } else {
            this.forward()
          }
        }

        // if (Math.abs(deltaY) > 60) {
        //   if (deltaY > 0) {
        //     this.doSwipeDown(event);
        //   } else {
        //     this.doSwipeUp(event);
        //   }
        // }
      }
    }
  }

  setGlider (): void {
    if (this.screenSizePx < 1024) return
    this.setStartAt()
    const imageArrLength = this.imageArr?.length || 0

    // dialog width is 80vw maxing at 1400px
    this.maxSlides = Math.floor((Math.min(this.screenSizePx * 0.8, 1400) - 80) / 120)

    if (imageArrLength >= 0 && imageArrLength < this.maxSlides) {
      this.showArrows = false
      this.boundAndFitGlider = true
    } else {
      this.showArrows = true
      this.boundAndFitGlider = false
    }
  }

  recreate (): void {
    setTimeout(() => {
      if (this.ngxGlide != null) {
        try {
          this.ngxGlide.recreate()
        } catch (error) {
        }
      }
    })
  }

  setStartAt (): void {
    this.startAt = this.index
  }
}
